import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { ArrowLeft, Eye, EyeOff, KeyRound, Phone, ShieldCheck, Ticket, Users } from 'lucide-react'
import { api, ApiError, saveSession } from '../lib/api'
import { useTransientText } from '../lib/useTransient'
import type { AuthSession } from '../lib/types'
import { Logo } from '../components/Logo'

const InfoCenter = lazy(() => import('../components/InfoCenter').then(m => ({ default: m.InfoCenter })))

type AuthMode = 'login' | 'register' | 'reset'
type LoginMethod = 'password' | 'code'
type ServerAuthMode = 'sms' | 'invite'

function deviceName() {
  const mobile = /iPhone|Android/i.test(navigator.userAgent)
  return `${mobile ? '手机浏览器' : '电脑浏览器'} · ${navigator.platform || 'Web'}`
}

function inviteCodeChars(value: string) {
  return value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 12)
}

function readableInviteCode(value: string) {
  return inviteCodeChars(value).replace(/(.{4})(?=.)/g, '$1-')
}

/** joinHint：从邀请链接进来（/join/<token>），登录后会自动进入「加入群聊」。 */
export function AuthPage({ onAuthenticated, joinHint = false }: { onAuthenticated: (session: AuthSession) => void; joinHint?: boolean }) {
  const [infoView, setInfoView] = useState<'terms' | 'privacy' | 'support' | null>(null)
  const [serverMode, setServerMode] = useState<ServerAuthMode | null>(null)
  const [healthError, setHealthError] = useState(false)
  const [healthRetry, setHealthRetry] = useState(0)
  const [mode, setMode] = useState<AuthMode>('login')
  const [method, setMethod] = useState<LoginMethod>('code')
  const [phone, setPhone] = useState('')
  const [account, setAccount] = useState('')
  const [code, setCode] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [resetCode, setResetCode] = useState('')
  const [password, setPassword] = useState('')
  const [nickname, setNickname] = useState('')
  const [acceptedPolicies, setAcceptedPolicies] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [developmentCode, setDevelopmentCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useTransientText()
  const [success, setSuccess] = useState('')
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    let cancelled = false
    setHealthError(false)
    api.health()
      .then((data) => { if (!cancelled) setServerMode(data.authMode) })
      .catch(() => { if (!cancelled) setHealthError(true) })
    return () => { cancelled = true }
  }, [healthRetry])

  useEffect(() => {
    if (countdown <= 0) return
    const timer = window.setInterval(() => setCountdown((value) => value - 1), 1000)
    return () => window.clearInterval(timer)
  }, [countdown])

  // 键盘弹出后 iOS 只会把输入框滚进视野，「登录」按钮仍可能被键盘盖住 —— 补一次滚动把它露出来。
  useEffect(() => {
    const form = formRef.current
    if (!form) return
    let scrollTimer: number | undefined
    const onFocusIn = (event: FocusEvent) => {
      const input = event.target
      if (!(input instanceof HTMLElement) || !input.matches('input:not([type="checkbox"]):not([type="radio"]), textarea')) return
      window.clearTimeout(scrollTimer)
      scrollTimer = window.setTimeout(() => {
        scrollTimer = undefined
        if (document.activeElement === input) {
          form.querySelector('.auth-submit')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      }, 320)
    }
    form.addEventListener('focusin', onFocusIn)
    return () => {
      window.clearTimeout(scrollTimer)
      form.removeEventListener('focusin', onFocusIn)
    }
  }, [mode])

  const inviteMode = serverMode === 'invite'
  const purpose = mode === 'register' ? 'register' : mode === 'reset' ? 'reset' : 'login'
  const requiresCode = serverMode === 'sms' && (mode !== 'login' || method === 'code')

  async function requestCode() {
    setError(''); setSuccess('')
    if (mode === 'register' && !acceptedPolicies) { setError('请先阅读并勾选同意用户协议和隐私政策'); return }
    if (!/^1\d{10}$/.test(phone)) { setError('请输入正确的 11 位手机号'); return }
    setBusy(true)
    try {
      const result = await api.requestCode(phone, purpose)
      setCountdown(60)
      setDevelopmentCode(result.developmentCode ?? '')
      if (result.developmentCode) setCode(result.developmentCode)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : '验证码发送失败')
    } finally { setBusy(false) }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(''); setSuccess('')
    if (mode === 'register' && !acceptedPolicies) { setError('请先阅读并勾选同意用户协议和隐私政策'); return }
    if (inviteMode) {
      if (mode === 'register') {
        if (inviteCodeChars(inviteCode).length !== 12) { setError('请输入完整的邀请码（XXXX-XXXX-XXXX）'); return }
        if (nickname.trim().length < 2 || nickname.trim().length > 20) { setError('昵称需要 2—20 个字'); return }
        if (password.length < 8) { setError('密码至少需要 8 位'); return }
      } else {
        if (!account.trim()) { setError('请输入诗序号'); return }
        if (mode === 'login' && !password) { setError('请输入密码'); return }
        if (mode === 'reset' && password.length < 8) { setError('新密码至少需要 8 位'); return }
        if (mode === 'reset' && inviteCodeChars(resetCode).length !== 12) { setError('请输入完整的重置码（XXXX-XXXX-XXXX）'); return }
      }
      setBusy(true)
      try {
        if (mode === 'reset') {
          await api.resetPasswordInvite(account.trim(), readableInviteCode(resetCode), password)
          setMode('login'); setResetCode(''); setPassword(''); setSuccess('密码已重置，请使用新密码登录')
          return
        }
        const session = mode === 'register'
          ? await api.registerInvite({ inviteCode: readableInviteCode(inviteCode), nickname: nickname.trim(), password, deviceName: deviceName() })
          : await api.loginPassword(account.trim(), password, deviceName())
        saveSession(session)
        onAuthenticated(session)
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : '操作失败，请稍后再试')
      } finally { setBusy(false) }
      return
    }
    if (mode === 'login' && method === 'password') {
      if (!/^(1\d{10}|\d{6})$/.test(account.trim())) { setError('请输入正确的手机号或 6 位诗序号'); return }
      if (!password) { setError('请输入密码'); return }
    } else {
      if (!/^1\d{10}$/.test(phone)) { setError('请输入正确的 11 位手机号'); return }
      if (requiresCode && !/^\d{6}$/.test(code)) { setError('请输入 6 位验证码'); return }
    }
    if (mode !== 'login' && password.length < 8) { setError('密码至少需要 8 位'); return }
    if (mode === 'register' && (nickname.trim().length < 2 || nickname.trim().length > 20)) { setError('昵称需要 2—20 个字'); return }
    setBusy(true)
    try {
      if (mode === 'reset') {
        await api.resetPassword(phone, code, password)
        setMode('login'); setMethod('password'); setCode(''); setPassword(''); setSuccess('密码已重置，请使用新密码登录')
        return
      }
      const session = mode === 'register'
        ? await api.register({ phone, code, password, nickname: nickname.trim(), deviceName: deviceName() })
        : method === 'password'
          ? await api.loginPassword(account.trim(), password, deviceName())
          : await api.loginCode(phone, code, deviceName())
      saveSession(session)
      onAuthenticated(session)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : '操作失败，请稍后再试')
    } finally { setBusy(false) }
  }

  function switchMode(next: AuthMode) {
    setMode(next); setAcceptedPolicies(false); setError(''); setSuccess(''); setCode(''); setPassword(''); setDevelopmentCode(''); setCountdown(0)
  }

  const title = serverMode === null ? '欢迎来到诗序' : mode === 'register'
    ? (inviteMode ? '邀请码注册' : '创建诗序账号')
    : mode === 'reset'
      ? '找回密码'
      : inviteMode ? '诗序号登录' : '手机号登录'
  const subtitle = serverMode === null ? '连接服务后即可登录' : mode === 'register'
    ? '与更多诗友，一起读诗。'
    : mode === 'reset'
      ? (inviteMode ? '输入管理员提供的重置码，重设密码' : '验证手机号后重新设置密码')
      : inviteMode ? '登录后，遇见更多诗友' : '登录后，遇见更多诗友'

  return <main className="auth-page">
    <section className="auth-phone">
      <div className="auth-landscape" aria-hidden="true"><span className="sun" /><span className="mountain m1" /><span className="mountain m2" /></div>
      <Logo />
      <form className="auth-card" ref={formRef} onSubmit={submit}>
        {joinHint ? <p className="join-hint" role="status"><Users size={15} />登录后将加入群聊</p> : null}
        <div className="auth-title">
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        {mode !== 'login' ? <button className="auth-back" type="button" onClick={() => switchMode('login')}><ArrowLeft size={20} /> 返回登录</button> : null}
        {serverMode === null ? healthError
          ? <p className="form-error" role="alert">无法连接诗序服务，请检查网络后<button className="inline-link" type="button" onClick={() => setHealthRetry((value) => value + 1)}>重试</button></p>
          : <p className="dev-code">正在连接诗序服务…</p> : null}
        {serverMode === 'sms' && mode === 'login' ? <div className="segmented auth-segmented">
          <button type="button" className={method === 'code' ? 'active' : ''} onClick={() => setMethod('code')}>验证码登录</button>
          <button type="button" className={method === 'password' ? 'active' : ''} onClick={() => setMethod('password')}>密码登录</button>
        </div> : null}
        {mode === 'register' && inviteMode ? <label className="field"><span>邀请码</span><div><Ticket size={18} /><input value={inviteCode} onChange={(event) => setInviteCode(readableInviteCode(event.target.value))} placeholder="XXXX-XXXX-XXXX" autoComplete="off" spellCheck={false} /></div></label> : null}
        {serverMode !== null && mode === 'register' ? <label className="field"><span>昵称</span><div><ShieldCheck size={18} /><input value={nickname} onChange={(event) => setNickname(event.target.value)} placeholder="2—20 个字" autoComplete="nickname" /></div></label> : null}
        {inviteMode
          ? (mode !== 'register' ? <label className="field"><span>诗序号</span><div><Phone size={18} /><input value={account} onChange={(event) => setAccount(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6 位诗序号，如 100001" inputMode="numeric" /></div></label> : null)
          : serverMode === null ? null : mode === 'login' && method === 'password'
            ? <label className="field"><span>手机号或诗序号</span><div><Phone size={18} /><input value={account} onChange={(event) => setAccount(event.target.value.replace(/\D/g, '').slice(0, 11))} placeholder="手机号或 6 位诗序号" inputMode="numeric" autoComplete="username" /></div></label>
            : <label className="field"><span>手机号</span><div><Phone size={18} /><input value={phone} onChange={(event) => setPhone(event.target.value.replace(/\D/g, '').slice(0, 11))} placeholder="请输入手机号" inputMode="tel" autoComplete="tel" /></div></label>}
        {mode === 'reset' && inviteMode ? <label className="field"><span>重置码</span><div><Ticket size={18} /><input value={resetCode} onChange={(event) => setResetCode(readableInviteCode(event.target.value))} placeholder="XXXX-XXXX-XXXX" autoComplete="off" spellCheck={false} /></div></label> : null}
        {requiresCode ? <label className="field"><span>验证码</span><div><ShieldCheck size={18} /><input value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6 位验证码" inputMode="numeric" autoComplete="one-time-code" /><button className="code-button" type="button" disabled={busy || countdown > 0} onClick={requestCode}>{countdown > 0 ? `${countdown}s` : '获取验证码'}</button></div></label> : null}
        {serverMode !== null && (mode === 'register' || mode === 'reset' || (mode === 'login' && (inviteMode || method === 'password'))) ? <label className="field"><span>{mode === 'reset' ? '新密码' : '密码'}</span><div><KeyRound size={18} /><input value={password} onChange={(event) => setPassword(event.target.value)} placeholder="至少 8 位" type={showPassword ? 'text' : 'password'} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} /><button className="field-icon" type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? '隐藏密码' : '显示密码'}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div></label> : null}
        {developmentCode ? <p className="dev-code">开发验证码已自动填写：{developmentCode}</p> : null}
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        {success ? <p className="form-success" role="status">{success}</p> : null}
        {mode === 'register' ? <div className="auth-consent"><label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}><input type="checkbox" checked={acceptedPolicies} onChange={event => { setAcceptedPolicies(event.target.checked); setError('') }} style={{ flex: '0 0 auto', width: 18, height: 18, marginTop: 3, accentColor: '#648c7f' }} /><span>我已阅读并同意用户协议和隐私政策</span></label><p>请阅读<button type="button" className="auth-policy-link" onClick={() => setInfoView('terms')}>用户协议</button>及<button type="button" className="auth-policy-link" onClick={() => setInfoView('privacy')}>隐私政策</button>后，再勾选并注册。</p></div> : null}
        <button className="primary-button auth-submit" disabled={busy || serverMode === null}>{busy ? '请稍候…' : mode === 'register' ? '注册并登录' : mode === 'reset' ? '重置密码' : '登录'}</button>
        {serverMode !== null && mode === 'login' ? <div className="auth-links"><button type="button" onClick={() => switchMode('register')}>{inviteMode ? '使用邀请码注册' : '注册账号'}</button><button type="button" onClick={() => switchMode('reset')}>忘记密码</button></div> : null}
      </form>
      <div className="auth-foot">请妥善保管账号与密码，勿向他人透露验证码。<div className="legal-links"><button type="button" className="auth-policy-link" onClick={() => setInfoView('terms')}>用户协议</button><span>·</span><button type="button" className="auth-policy-link" onClick={() => setInfoView('privacy')}>隐私政策</button><span>·</span><button type="button" className="auth-policy-link" onClick={() => setInfoView('support')}>帮助与反馈</button></div></div>
    </section>
    {infoView ? <Suspense fallback={<p role="status">正在打开说明…</p>}><InfoCenter initial={infoView} token="" onClose={() => setInfoView(null)} /></Suspense> : null}
  </main>
}
